// Results render (Figma): Final Score circle, Score Analysis, Recommendations. The Actual
// Observed Performance bars are painted by the PerformanceTable (shared aligned grid).
// ALL transcript-/model-derived text via textContent (§A.3.5).

import type { AggregateResult, CallOwnership, SkillBreakdownRow, HighLevelGraph, SkillBand } from '../lib/types.js';
import { displaySkillName } from '../lib/skills.js';
import { improvementGaps } from './improvement.js';
import { setText } from '../lib/escape.js';
import { bonusInfoIcon, coverageText, formatBonus } from '../lib/bonusCopy.js';
import { renderCrossSkillDemo } from './crossSkillGraph.js';
import { renderCrossSkillZones } from './crossSkillImpact.js';
import { wireCollapse } from '../lib/collapse.js';

/** Small DOM helper: element with optional class + safe text. */
function el(tag: string, cls?: string, text?: string): HTMLElement {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) setText(n, text);
  return n;
}

// Collapsible sections (What worked well / Areas / Who was scored / Recommendations) — default
// collapsed, state persisted per device (see lib/collapse.ts).

/** Split the scored skills into strengths (met/beat target, or a bonus) and gaps (scored shortfalls). */
function splitSkills(r: AggregateResult): { worked: SkillBreakdownRow[]; gaps: SkillBreakdownRow[] } {
  const excused = (s: SkillBreakdownRow): boolean => s.expected_score === 0;
  const didIt = (s: SkillBreakdownRow): boolean => s.evidence_state === 'scored' && (s.actual_score ?? 0) > 0;
  const worked = r.skill_breakdown.filter((s) => (s.attainment !== null && s.attainment >= 100) || (excused(s) && didIt(s)));
  const gaps = improvementGaps(r.skill_breakdown);
  return { worked, gaps };
}

const graphOf = (graphs: HighLevelGraph[], kind: 'lift' | 'drag'): HighLevelGraph | null => graphs.find((g) => g.kind === kind) ?? null;
const nodeName = (g: HighLevelGraph, id: string | undefined): string => (id ? displaySkillName(g.nodes.find((n) => n.id === id)?.name ?? '') : '');

/** The connected-skill story folded into a per-skill line: its role in the chain, in plain language.
 *  Empty string when the skill is standalone (the caller adds a short "standalone" tag instead). */
function connectedNote(id: string, graphs: HighLevelGraph[]): string {
  const drag = graphOf(graphs, 'drag');
  const lift = graphOf(graphs, 'lift');
  const dNode = drag?.nodes.find((n) => n.id === id);
  if (drag && dNode) {
    const rootName = nodeName(drag, drag.root);
    if (dNode.role === 'root') return 'Root cause of the chain that held the call back — fix this first and the skills below it lift with it.';
    if (dNode.role === 'flagship') return `The outcome this chain reached — strengthen the root (${rootName}) and this lifts with it.`;
    return `Downstream of ${rootName} — it had little to build on until the root is fixed.`;
  }
  const lNode = lift?.nodes.find((n) => n.id === id);
  if (lift && lNode) {
    const rootName = nodeName(lift, lift.root);
    const flagName = nodeName(lift, lift.flagship);
    if (lNode.role === 'root') return `Set up the winning chain that followed, up to ${flagName}.`;
    if (lNode.role === 'flagship') return `Where the good work paid off — built on ${rootName} before it.`;
    return `Part of the chain that lifted the call, feeding ${flagName}.`;
  }
  return '';
}

/** One Score-Analysis item with the connected story folded in: name + score + role flag, the model's
 *  summary, then the connected note (or a short "standalone" tag when the skill formed no chain). */
function foldedItem(s: SkillBreakdownRow, graphs: HighLevelGraph[], strength: boolean): HTMLElement {
  const item = el('div', 'cs-anal-item');
  const row = el('div', 'cs-anal-row');
  row.appendChild(el('span', 'cs-anal-nm', displaySkillName(s.skill_name)));
  const scored = s.actual_score !== null;
  row.appendChild(el('span', 'cs-anal-sc', `${scored ? s.actual_score!.toFixed(1) : '—'} / ${s.expected_score || 'opt'}`));

  const drag = graphOf(graphs, 'drag');
  const dNode = drag?.nodes.find((n) => n.id === s.skill_id);
  const meets = (s.attainment !== null && s.attainment >= 100) || (s.expected_score === 0 && s.evidence_state === 'scored');
  let flagTxt = '', flagCls = 'gap';
  if (dNode?.role === 'root') flagTxt = 'root cause';
  else if (dNode?.role === 'flagship') flagTxt = 'outcome';
  else if (meets) { flagTxt = s.expected_score === 0 ? 'bonus' : 'meets'; flagCls = 'meets'; }
  else if (scored) flagTxt = 'below';
  else flagTxt = 'no evidence';
  row.appendChild(el('span', `cs-anal-flag ${flagCls}`, flagTxt));
  item.appendChild(row);

  if (s.summary) item.appendChild(el('div', 'cs-anal-why', s.summary));
  const cn = connectedNote(s.skill_id, graphs);
  if (cn) {
    item.appendChild(el('div', 'cs-anal-link', cn));
  } else if (strength && s.skill_id === 'aha-moment') {
    // Option A: a strong-but-standalone A-Ha is the outcome, but it wasn't set up by the discovery chain.
    item.appendChild(el('div', 'cs-anal-link', 'Marked the Outcome — but it wasn’t set up by the discovery chain that usually produces it (that discovery came in weak here), so treat it as a real but fragile, unsupported A-Ha.'));
  } else {
    item.appendChild(el('div', 'cs-anal-link cs-anal-standalone', strength
      ? 'A standalone strength — it didn’t set up or depend on another skill on this call.'
      : 'A standalone gap — it didn’t feed the drag chain above.'));
  }
  return item;
}

/** A Score-Analysis section (worked / areas) built from folded items. Collapsible (default collapsed). */
function foldedSection(title: string, rows: SkillBreakdownRow[], graphs: HighLevelGraph[], strength: boolean, emptyMsg: string, key: string): HTMLElement {
  const wrap = el('div', 'analysis-section');
  const head = el('div', `analysis-title ${strength ? 'cs-anal-h-good' : 'cs-anal-h-bad'}`, `${title}:`);
  const body = el('div', 'analysis-body');
  if (!rows.length) body.appendChild(el('p', 'analysis-empty', emptyMsg));
  else for (const s of rows) body.appendChild(foldedItem(s, graphs, strength));
  wrap.append(head, body);
  wireCollapse(head, body, key, false); // Cross-Skill analysis categories start expanded
  return wrap;
}

// (toChip removed — the old chain chips are gone; Cross-Skill now renders via crossSkillImpact.ts)
import { navigateTo } from './sidenav.js';
import { renderTrendPanel, type TrendCall } from './trendOverlay.js';
import { isTrendsEnabled } from '../lib/settings.js';
import type { TrendPoint } from '../lib/api.js';
import type { PerformanceTable } from './performanceTable.js';

export interface ResultEls {
  circle: HTMLElement;
  meaning: HTMLElement;
  coverage: HTMLElement;
  /** Optional-skill bonus row (label + value). Optional so older hosts without the nodes still render. */
  bonusLabel?: HTMLElement | null;
  bonus?: HTMLElement | null;
  analysis: HTMLElement;
  recommendations: HTMLElement;
  crossSkill: HTMLElement; // Cross-Skill Diagnostic view, hosts the connected-skills (High-Level) summary
  trend?: HTMLElement; // Recent-trend overlay (optional host; single-call fallback until the series is wired)
}

/** Render the connected-skills (High-Level) summary into the Cross-Skill Diagnostic view. Moved out
 *  of the Score Analysis box so the cross-skill insight lives on its own page. */
/** Idle state (no run yet): two halves — the "what this lens does" text on the left, a decorative
 *  connected-skills web on the right. Replaced by the real output the moment a run's data is ready
 *  (independent of the other two lenses). Exported so main.ts can paint it on load. */
export function renderCrossSkillIdle(host: HTMLElement): void {
  host.innerHTML = '';
  const grid = document.createElement('div');
  grid.className = 'cs-idle';
  const left = document.createElement('div');
  left.className = 'cs-idle-left';
  const lede = document.createElement('p');
  lede.className = 'cs-idle-lede';
  setText(lede, 'The Cross-Skill Diagnostic slices across the eleven skills to surface the relationships between them: how one skill lifts or drags the others, and the single root cause to fix first.');
  left.appendChild(lede);
  const hint = document.createElement('p');
  hint.className = 'cs-idle-hint';
  setText(hint, 'Import a call and hit Run in the Diagnostic Controls above; the connected-skills story appears here automatically.');
  left.appendChild(hint);
  // Full-walkthrough link, pinned to the bottom of the column (margin-top:auto in CSS).
  const guideP = document.createElement('p');
  guideP.className = 'cs-idle-guide';
  const guide = document.createElement('a');
  guide.className = 'bs-intro-link';
  guide.href = '#';
  guide.textContent = 'Read the full walkthrough in Getting Started';
  guide.addEventListener('click', (e) => {
    e.preventDefault();
    navigateTo('getting-started');
  });
  guideP.appendChild(guide);
  left.appendChild(guideP);
  grid.appendChild(left);
  const right = document.createElement('div');
  right.className = 'cs-idle-right';
  const vis = document.createElement('div');
  vis.className = 'cs-demo-wrap';
  renderCrossSkillDemo(vis);
  right.appendChild(vis);
  const cap = document.createElement('div');
  cap.className = 'cs-demo-cap';
  cap.textContent = 'How one skill lifts or drags the next';
  right.appendChild(cap);
  grid.appendChild(right);
  host.appendChild(grid);
}

function renderCrossSkill(host: HTMLElement, r: AggregateResult): void {
  host.innerHTML = '';
  if (!r.skill_breakdown.length) {
    const wrap = el('div', 'cs-noresult');
    wrap.appendChild(el('h4', 'cs-graph-title', 'Not enough connected-skill evidence'));
    wrap.appendChild(el('p', 'cs-noresult-text', 'This call was scored, but it didn’t surface enough to map how the skills lift and drag each other. The scores are on Quick Snapshot; score a fuller call to see the story here.'));
    host.appendChild(wrap);
    return;
  }
  // The 3-zone layout: Message Center | What moved the score (Ranked / Cards) | The Domino.
  renderCrossSkillZones(host, r);
}

/** Build the recent-trend overlay. Gated behind the Settings toggle (on for new tenants): when off we show
 *  a disclaimer + a link to Settings; when on we render the chart. `older` are the rep's prior same-
 *  call-type points from /api/trends (empty on the first render / when persistence is off) — the
 *  current call is always shown as the newest line. */
export function renderTrend(host: HTMLElement, r: AggregateResult, older: TrendPoint[] = []): void {
  if (!isTrendsEnabled()) {
    host.replaceChildren(trendDisclaimer());
    return;
  }
  const targets: Record<string, number | null> = {};
  const current: Record<string, number | null> = {};
  for (const s of r.skill_breakdown) {
    targets[s.skill_id] = s.expected_score;
    current[s.skill_id] = s.actual_score;
  }
  // Order-independent identity key — the DB returns skill keys in a different order than we build them,
  // so a plain JSON.stringify compare would fail to recognize the just-persisted current call.
  const canon = (scores: Record<string, number | null>, final: number): string =>
    Object.keys(scores).sort().map((k) => `${k}:${scores[k]}`).join(',') + `|${final}`;
  const curKey = canon(current, r.final_score);
  // Older points, oldest→newest; drop any that equal the current call (the just-persisted duplicate,
  // or a race where persistence beat this fetch) so it isn't drawn twice.
  const calls: TrendCall[] = older
    .filter((p) => canon(p.scores, p.final_score) !== curKey)
    .map((p) => ({ label: fmtDay(p.ts), meta: `${fmtDay(p.ts)} · ${p.call_type ?? ''} · final ${p.final_score}`, scores: p.scores }));
  const type = r.call_meta?.call_type ? `${r.call_meta.call_type} · ` : '';
  calls.push({ label: 'This call', meta: `This call · ${type}final ${r.final_score}`, scores: current });
  renderTrendPanel(host, { calls, targets });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDay(ts: number): string {
  const d = new Date(ts);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Trends are off — enable in Settings" note, with a link that switches to the Settings view. */
function trendDisclaimer(): HTMLElement {
  const p = document.createElement('p');
  p.className = 'trend-disclaimer';
  p.append('Trends are off. ');
  const a = document.createElement('a');
  a.href = '#';
  a.className = 'trend-settings-link';
  a.textContent = 'Enable in Settings';
  a.addEventListener('click', (e) => {
    e.preventDefault();
    (document.querySelector('.nav-item[data-view="account"]') as HTMLElement | null)?.click();
  });
  p.append(a);
  p.append(' to see how this rep’s scores track across their recent scored calls.');
  return p;
}

function scoreClass(final: number, capped?: boolean): string {
  if (capped || final < 70) return 'circle-bad';
  if (final < 100) return 'circle-mid';
  return 'circle-good';
}

export function renderResults(els: ResultEls, table: PerformanceTable, r: AggregateResult): void {
  els.circle.className = `score-circle ${scoreClass(r.final_score, r.capped)}`;
  setText(els.circle, String(r.final_score));
  setText(els.meaning, r.meaning_label);
  // Coverage/confidence ONLY. The optional-skill bonus is a separate fact and gets its own row —
  // appending it here made it read as something high confidence earned. See lib/bonusCopy.ts.
  setText(els.coverage, coverageText(r.evidence_coverage, r.confidence_label));
  // Always rendered, including at zero ("None"). Hiding the row on a scorecard with no optional
  // skills left no way to tell "nothing to credit" apart from "the bonus line is broken".
  const bonus = r.optional_bonus ?? 0;
  if (els.bonus) {
    setText(els.bonus, formatBonus(bonus));
    els.bonus.hidden = false;
  }
  if (els.bonusLabel) {
    // The (i) is injected here rather than written into index.html so the wording lives only in
    // lib/bonusCopy.ts. Guarded: renderResults runs again on every re-aggregate.
    if (!els.bonusLabel.querySelector('.meta-info')) els.bonusLabel.appendChild(bonusInfoIcon());
    els.bonusLabel.hidden = false;
  }

  table.renderActual(r.skill_breakdown);

  // Recent-trend overlay. Until the multi-call series is wired (persistence + rep identity), this
  // shows the single fallback layer: THIS call's skill profile vs the gold dotted expected minimum —
  // both already in skill_breakdown, so it always renders.
  if (els.trend) renderTrend(els.trend, r);

  // The connected-skills (High-Level) summary now lives on the Cross-Skill Diagnostic page, not here.
  renderCrossSkill(els.crossSkill, r);

  // Score Analysis (now on the Cross-Skill page) — the per-skill assessment with the connected story
  // folded into each item (its role in the chain, or a "standalone" tag). Next steps live solely in
  // the Recommendations box. The exclusion + shortfall rules live in splitSkills / improvement.ts.
  els.analysis.innerHTML = '';
  const graphs = r.high_level_summary?.graphs ?? [];
  const { worked, gaps } = splitSkills(r);
  // Keys bumped to v2 so the new expanded-by-default takes effect even where an old 'collapsed' was stored.
  els.analysis.appendChild(foldedSection('What worked well', worked, graphs, true, 'No standout strengths on this call.', 'worked-v2'));
  els.analysis.appendChild(foldedSection('Areas requiring improvement', gaps, graphs, false, 'None. Every scored skill met or beat its target on this call.', 'areas-v2'));

  // Multi-rep calls: explain who was scored and why (transparency to pre-empt disagreement).
  const multiRep = Boolean(r.call_ownership?.multi_rep);
  if (multiRep && r.call_ownership) {
    els.analysis.appendChild(ownershipSection(r.call_ownership));
  }
  // The RACI key in the Score Analysis header only makes sense when the ownership list (with its
  // R/A/C/I letters) is shown, so it rides the same multi-rep condition.
  const raciLegend = document.getElementById('raci-legend');
  if (raciLegend) raciLegend.hidden = !multiRep;

  // Recommendations = the SINGLE unified next-steps list. De-duplicated across skills (different
  // skills often surface the same coaching item) so the box reads as one comprehensive plan.
  els.recommendations.innerHTML = '';
  const ul = document.createElement('ul');
  ul.className = 'rec-list';
  const seen = new Set<string>();
  for (const s of r.skill_breakdown) {
    for (const rec of s.recommendations ?? []) {
      const key = rec.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const li = document.createElement('li');
      setText(li, rec);
      ul.appendChild(li);
    }
  }
  if (!ul.childElementCount) {
    const li = document.createElement('li');
    setText(li, 'No specific recommendations for this call.');
    ul.appendChild(li);
  }
  els.recommendations.appendChild(ul);

  // Recommendations is collapsible via its column header (static in the HTML). Wire it once; the
  // collapsed state lives on the persistent #recommendations box, so it survives content re-renders.
  const recsHead = document.querySelector<HTMLElement>('.cs-ah-recs .col-h');
  if (recsHead && !recsHead.dataset.collapseWired) {
    recsHead.dataset.collapseWired = '1';
    wireCollapse(recsHead, els.recommendations, 'recs');
  }
}

/** On-screen "Who was scored — and why" — methodology + RACI determination (multi-rep calls).
 *  Collapsible (default collapsed), like the other Score-Analysis sections. */
function ownershipSection(o: CallOwnership): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'analysis-section';
  const h = document.createElement('div');
  h.className = 'analysis-title';
  setText(h, 'Who was scored, and why:');
  const body = el('div', 'analysis-body');
  const p = document.createElement('p');
  p.className = 'summary-para';
  setText(
    p,
    'More than one rep from your side was on this call, so the scorecard reflects the call owner, the rep ' +
      'driving the conversation (RACI R/A), not supporting voices, and never the buyer.' +
      (o.primary_rep ? ` Scored: ${o.primary_rep} (${o.confidence} confidence).` : ''),
  );
  body.appendChild(p);
  const ul = document.createElement('ul');
  for (const part of o.participants) {
    const li = document.createElement('li');
    setText(li, `${part.name}, ${part.role} (${part.raci})${part.note ? `: ${part.note}` : ''}`);
    ul.appendChild(li);
  }
  for (const b of o.buyer_side) {
    const li = document.createElement('li');
    setText(li, `${b}, ${buyerLabel(o, b)}`);
    ul.appendChild(li);
  }
  body.appendChild(ul);
  wrap.append(h, body);
  wireCollapse(h, body, 'who');
  return wrap;
}

/** How to label a buyer in the ownership list: a supporting/passive buyer carries its RACI role
 *  ("Buyer (C), not scored"); everyone else is a plain "Buyer (not scored)". */
function buyerLabel(o: CallOwnership, name: string): string {
  const role = o.buyer_side_roles?.find((x) => x.name === name);
  return role ? `Buyer (${role.raci}), not scored` : 'Buyer (not scored)';
}

export function clearResults(els: ResultEls): void {
  els.circle.className = 'score-circle';
  els.circle.textContent = '';
  els.meaning.textContent = '';
  els.coverage.textContent = '';
  els.analysis.innerHTML = '';
  els.recommendations.innerHTML = '';
  els.trend?.replaceChildren();
  renderCrossSkillIdle(els.crossSkill);
}
